import { FC, useEffect, useMemo, useState } from 'react'

import { useCoAgent, useCopilotAction } from '@copilotkit/react-core'
import { useAiChat } from 'modules/ai/chat/useAiChat'

import i18n from 'modules/core/lib/i18n'
import type { OneSharedState } from 'modules/ai/lib/useOne'

type TargetPolicyOption = {
  id: string
  name: string
}

const buildFallbackOneState = (): OneSharedState => ({
  platform: 'web',
  requiredSkills: [],
  coCreationData: null,
  files: null,
  currentPage: {
    url: window.location.href,
    title: document.title,
  },
})

type SetOneState = (
  newState: OneSharedState | ((prevState: OneSharedState | undefined) => OneSharedState)
) => void

function isRecordOfUnknown(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function isStringField(value: unknown): value is string {
  return typeof value === 'string'
}

function parseTargetPolicyOption(value: unknown): TargetPolicyOption | null {
  if (!isRecordOfUnknown(value)) return null

  const { id, name } = value
  if (!isStringField(id) || !isStringField(name)) return null

  return { id, name }
}

function parseTargetPolicyOptions(value: unknown): TargetPolicyOption[] | null {
  if (!Array.isArray(value)) return null

  const parsedOptions: TargetPolicyOption[] = []
  for (const raw of value) {
    const parsed = parseTargetPolicyOption(raw)
    if (!parsed) return null
    parsedOptions.push(parsed)
  }

  return parsedOptions
}

const TimeoffPolicySelectionAction: FC<{
  employeeName: string
  targetPolicyOptions: unknown
  respond: (message: string) => void
  setOneState: SetOneState
}> = ({ employeeName, targetPolicyOptions, respond, setOneState }) => {
  const { appendMessages, setClarifyingQuestion } = useAiChat()
  const parsedTargetPolicyOptions = useMemo(
    () => parseTargetPolicyOptions(targetPolicyOptions),
    [targetPolicyOptions]
  )
  const [selectedPolicyId, setSelectedPolicyId] = useState(parsedTargetPolicyOptions?.[0]?.id ?? '')

  useEffect(() => () => setClarifyingQuestion(null), [setClarifyingQuestion])

  useEffect(() => {
    if (!parsedTargetPolicyOptions || parsedTargetPolicyOptions.length === 0) {
      respond('Could not render policy selector because the policy options are invalid.')
      return
    }

    const selectedPolicy = parsedTargetPolicyOptions.find(
      (policy) => policy.id === selectedPolicyId
    )

    setClarifyingQuestion({
      currentStep: {
        question: i18n.t('common.ai.policy_change.policy_selection_question', {
          employee: employeeName,
        }),
        options: parsedTargetPolicyOptions.map((policy) => ({ id: policy.id, label: policy.name })),
        selectionMode: 'single',
        optional: false,
        allowCustomAnswer: false,
        selectedOptionIds: selectedPolicyId ? [selectedPolicyId] : [],
        isCustomAnswerActive: false,
      },
      currentStepIndex: 0,
      totalSteps: 1,
      toggleOption: setSelectedPolicyId,
      confirm: () => {
        if (!selectedPolicy) return

        setClarifyingQuestion(null)
        setOneState((previous) => {
          const currentState = previous ?? buildFallbackOneState()

          return {
            ...currentState,
            requiredSkills: Array.from(
              new Set([...currentState.requiredSkills, 'timeoffPolicyChange'])
            ),
            coCreationData: {
              ...(currentState.coCreationData ?? {}),
              timeoffPolicyChange: {
                employeeName,
                targetPolicyId: selectedPolicy.id,
                targetPolicyName: selectedPolicy.name,
                awaiting: 'effectiveAt',
              },
            },
          }
        })
        appendMessages([
          {
            role: 'assistant',
            content: `${i18n.t('common.ai.policy_change.effective_date_only_question', {
              employee: employeeName,
              policy: `**${selectedPolicy.name}**`,
            })}`,
          },
        ])
        respond(
          `TARGET_POLICY_SELECTED: employeeName="${employeeName}" targetPolicyId="${selectedPolicy.id}" targetPolicyName="${selectedPolicy.name}". NEXT_STEP: ask only for effectiveAt in chat. DO_NOT_CALL: selectTimeoffPolicy.`
        )
      },
      skip: () => {},
      cancel: () => {
        setClarifyingQuestion(null)
        respond('Policy selection cancelled')
      },
      back: () => {},
      setCustomAnswerText: () => {},
      setCustomAnswerActive: () => {},
      activateCustomAnswer: () => {},
    })
  }, [
    appendMessages,
    employeeName,
    respond,
    selectedPolicyId,
    setClarifyingQuestion,
    setOneState,
    parsedTargetPolicyOptions,
  ])

  return null
}

export const useSelectTimeoffPolicyAction = () => {
  const { setState } = useCoAgent<OneSharedState>({ name: 'one-workflow' })

  useCopilotAction({
    name: 'selectTimeoffPolicy',
    available: 'remote',
    followUp: false,
    description:
      'Ask the user to choose one target Time Off policy from a list. This renders a single-step clarifying question and responds with targetPolicyId and targetPolicyName. Do not use this action to ask for the effective date. Do not use markdown emphasis for generic text like "new time-off policy".',
    parameters: [
      { name: 'employeeName', required: true },
      {
        name: 'targetPolicyOptions',
        type: 'object[]',
        required: true,
        description: 'Candidate target policies. Each option must include id and name.',
      },
    ],
    renderAndWaitForResponse: ({ status, args, respond }) => {
      if (status !== 'executing') return <></>

      const employeeName = isStringField(args.employeeName) ? args.employeeName : null
      if (!employeeName) {
        respond('Could not render policy selector because the policy options are invalid.')
        return <></>
      }

      return (
        <TimeoffPolicySelectionAction
          employeeName={employeeName}
          targetPolicyOptions={args.targetPolicyOptions}
          respond={respond}
          setOneState={setState}
        />
      )
    },
  })
}
